/**
 * 匯率表 /exchange-rates（20260611 反饋批次）
 * 6 幣別固定列（HKD 中心制：1 HKD = rate × 幣別）；建單時鎖快照，調整不影響既有訂單。
 */
import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { formatDateTime } from "../../lib/utils";
import type { Loc } from "../../../../shared/const";
import { EXCHANGE_CURRENCIES, type ExchangeCurrency } from "../../../../shared/booking";
import { THEAD_ROW } from "./base-data-shared";

const CURRENCY_LOC: Record<ExchangeCurrency, Loc> = {
  TWD: { "zh-TW": "新台幣", "en-US": "New Taiwan Dollar", "zh-CN": "新台币" },
  KRW: { "zh-TW": "韓元", "en-US": "Korean Won", "zh-CN": "韩元" },
  EUR: { "zh-TW": "歐元", "en-US": "Euro", "zh-CN": "欧元" },
  USD: { "zh-TW": "美元", "en-US": "US Dollar", "zh-CN": "美元" },
  JPY: { "zh-TW": "日圓", "en-US": "Japanese Yen", "zh-CN": "日元" },
  CNY: { "zh-TW": "人民幣", "en-US": "Chinese Yuan", "zh-CN": "人民币" },
};

type Row = { currency: ExchangeCurrency; rate: string; updatedAt: Date };

export default function ExchangeRates() {
  const { t } = useLanguage();
  const listQ = trpc.exchangeRate.list.useQuery();
  const rows = (listQ.data ?? []) as Row[];
  const byCurrency = new Map(rows.map((r) => [r.currency, r]));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-medium tracking-tight">
          {t({ "zh-TW": "匯率表", "en-US": "Exchange Rates", "zh-CN": "汇率表" })}
        </h1>
        <p className="text-[13px] text-muted-foreground">
          {t({
            "zh-TW": "港幣中心制：1 HKD = rate × 幣別。建單時鎖匯率快照，之後調整不影響既有訂單。",
            "en-US": "HKD-centric: 1 HKD = rate × currency. Rates are snapshotted at booking — changes never affect existing orders.",
            "zh-CN": "港币中心制：1 HKD = rate × 币别。建单时锁汇率快照，之后调整不影响既有订单。",
          })}
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className={THEAD_ROW}>
                  <th className="p-3">{t({ "zh-TW": "幣別", "en-US": "Currency", "zh-CN": "币别" })}</th>
                  <th className="p-3">{t({ "zh-TW": "匯率", "en-US": "Rate", "zh-CN": "汇率" })}</th>
                  <th className="p-3">{t({ "zh-TW": "更新時間", "en-US": "Updated", "zh-CN": "更新时间" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "操作", "en-US": "Action", "zh-CN": "操作" })}</th>
                </tr>
              </thead>
              <tbody>
                {EXCHANGE_CURRENCIES.map((cur) => {
                  const r = byCurrency.get(cur);
                  return (
                    <tr key={cur} className="border-b border-border/60 last:border-0">
                      <td className="p-3">
                        <span className="font-medium">{cur}</span>
                        <span className="ml-2 text-muted-foreground">{t(CURRENCY_LOC[cur])}</span>
                      </td>
                      <td className="p-3 numeric">
                        {r ? (
                          <>1 HKD = <span className="font-medium">{Number(r.rate)}</span> {cur}</>
                        ) : (
                          <span className="text-muted-foreground/60">{t({ "zh-TW": "未設定", "en-US": "unset", "zh-CN": "未设定" })}</span>
                        )}
                      </td>
                      <td className="p-3 text-muted-foreground">{r ? formatDateTime(r.updatedAt) : "—"}</td>
                      <td className="p-3 text-right">
                        <RateDialog currency={cur} existing={r} onDone={() => listQ.refetch()} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RateDialog({ currency, existing, onDone }: { currency: ExchangeCurrency; existing?: Row; onDone: () => void }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(existing ? Number(existing.rate) : 0);

  const m = trpc.exchangeRate.upsert.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  // 開啟時從 props/初始值重新同步草稿，避免殘留上一次取消的輸入
  const changeOpen = (o: boolean) => {
    setOpen(o);
    if (o) setRate(existing ? Number(existing.rate) : 0);
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        {existing ? (
          <Button variant="ghost" size="sm">
            <Pencil className="h-3.5 w-3.5" />
            {t({ "zh-TW": "編輯", "en-US": "Edit", "zh-CN": "编辑" })}
          </Button>
        ) : (
          <Button size="sm" variant="outline">
            <Plus className="h-3.5 w-3.5" />
            {t({ "zh-TW": "設定", "en-US": "Set", "zh-CN": "设定" })}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {currency} · {t(CURRENCY_LOC[currency])}
          </DialogTitle>
          <DialogDescription>
            {t({
              "zh-TW": "建單時鎖匯率快照，調整不影響既有訂單。",
              "en-US": "Rates are snapshotted at booking; changes never affect existing orders.",
              "zh-CN": "建单时锁汇率快照，调整不影响既有订单。",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>1 HKD =</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min={0} step="0.0001" value={rate} onChange={(e) => setRate(Number(e.target.value))} />
              <span className="text-sm text-muted-foreground">{currency}</span>
            </div>
          </div>
          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
          </Button>
          <Button onClick={() => m.mutate({ currency, rate })} disabled={m.isPending || !Number.isFinite(rate) || rate <= 0}>
            {t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "保存" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
